import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Activity, Mail, Lock, Eye, EyeOff, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { isValidPhone } from '../lib/utils';

const Register = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return t('login_error');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' ');
    }
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      formData.firstName.trim().toLowerCase() === formData.lastName.trim().toLowerCase()
    ) {
      setError(t('register_names_identical'));
      return;
    }

    if (!isValidPhone(formData.phone)) {
      setError(t('register_phone_invalid'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register_password_mismatch'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('register_password_short'));
      return;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError(t('register_password_no_uppercase'));
      return;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError(t('register_password_no_lowercase'));
      return;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError(t('register_password_no_digit'));
      return;
    }

    setLoading(true);

    try {
      const res = await register({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'patient'
      });

      toast.success(
        res?.message || 'Compte créé. Vérifiez votre adresse email avant de vous connecter.'
      );

      navigate('/login');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="flex items-center text-[#0596DE]">
              <Moon className="w-8 h-8" />
              <Activity className="w-8 h-8 -ml-2" />
            </div>
            <span className="font-bold text-2xl text-[#0596DE] font-['Outfit']">Tabbibak.com</span>
          </Link>

          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">
            {t('register_title')}
          </h1>
          <p className="text-[#64748B] mb-8">
            {t('register_subtitle')}
          </p>

          {error && (
            <div
              className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-[#0F172A]">{t('register_first_name')}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={t('register_first_name_placeholder')}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="first-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lastName" className="text-[#0F172A]">{t('register_last_name')}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder={t('register_last_name_placeholder')}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0F172A]">{t('register_email')}</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  className="pl-10 rounded-xl"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#0F172A]">{t('register_phone')}</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+213 5XX XX XX XX"
                  className="pl-10 rounded-xl"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0F172A]">{t('register_password')}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10 rounded-xl"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-[#64748B] mt-1">{t('register_password_rules')}</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#0F172A]">{t('register_confirm')}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 rounded-xl"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('register_loading') : t('register_btn')}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E2E8F0]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#F8FAFC] text-[#64748B]">{t('register_or')}</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full h-12"
            onClick={loginWithGoogle}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('register_google')}
          </Button>

          <p className="text-center text-[#64748B] mt-8">
            {t('register_has_account')}{' '}
            <Link to="/login" className="text-[#0596DE] hover:underline font-medium">
              {t('register_login')}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_tabbibak-algeria/artifacts/bch5rgxl_radja.jpg"
          alt="Medical clinic"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0596DE]/20"></div>
      </div>
    </div>
  );
};

export default Register;